import { RefObject, useCallback, useLayoutEffect, useState } from "react";

type ClientDomRect = Record<keyof Omit<DOMRect, "toJSON">, number>;

type Position = { x?: number; y?: number };

type TextSelectionState = {
  clientRect?: ClientDomRect;
  isCollapsed?: boolean;
  textContent?: string;
  position?: Position;
};

const defaultState: TextSelectionState = {} as const;

function roundValues(_rect: ClientDomRect) {
  const rect = {
    ..._rect,
  };

  for (const key of Object.keys(rect)) {
    rect[key as keyof ClientDomRect] = Math.round(
      rect[key as keyof ClientDomRect]
    );
  }

  return rect;
}

export const useTextSelection = (
  popupContainer?: RefObject<unknown>,
  target?: HTMLElement
) => {
  const [{ clientRect, isCollapsed, textContent }, setState] =
    useState<TextSelectionState>(defaultState);

  const handler = useCallback(
    (e: MouseEvent) => {
      if (
        popupContainer?.current &&
        (popupContainer.current as HTMLElement).contains(e.target as Node)
      ) {
        return;
      }

      let newRect: ClientDomRect;
      const selection = window.getSelection();
      let newState: TextSelectionState = {};

      if (!selection || !selection.rangeCount) {
        setState(newState);

        return;
      }

      const range = selection.getRangeAt(0);

      if (target && !target.contains(range.commonAncestorContainer)) {
        setState(newState);

        return;
      }

      if (!range) {
        setState(newState);

        return;
      }

      const contents = range.cloneContents();

      if (contents.textContent) {
        newState = {
          ...newState,
          textContent: contents.textContent,
        };
      }

      const rects = range.getClientRects();

      if (!rects.length && range.commonAncestorContainer) {
        const el = range.commonAncestorContainer as HTMLElement;

        newRect = roundValues(el.getBoundingClientRect().toJSON());
      } else {
        if (rects.length < 1) return;

        newRect = roundValues(rects[0].toJSON());
      }

      newState = {
        ...newState,
        clientRect: newRect,
        isCollapsed: range.collapsed,
      };

      if (!newState.textContent) {
        newState = {};
      }

      setState(newState);
    },
    [target, popupContainer]
  );

  useLayoutEffect(() => {
    document.addEventListener("mouseup", handler);

    return () => {
      document.removeEventListener("mouseup", handler);
    };
  }, [target]);

  return {
    clientRect,
    isCollapsed,
    textContent,
  };
};
